import { Fragment } from "react";
import { motion } from "framer-motion";
import { CloudSun, MessageSquare, ShieldCheck, ArrowRight, Waypoints } from "lucide-react";

const DAY = [
  { t: "04:00", d: "health hazards, breakdowns, job offers" },
  { t: "05:00", d: "the weather is drawn for the day" },
  { t: "06:30", d: "the school run" },
  { t: "09:00", d: "the town's finances move" },
  { t: "···", d: "buses every 20 minutes, through-traffic" },
  { t: "16:30", d: "school out" },
  { t: "18:30", d: "payroll on Fridays" },
  { t: "21:00", d: "the newspaper is written on Fridays" },
  { t: "23:51", d: "the chronicle is compiled" },
  { t: "02:30", d: "residents reflect on their day" },
  { t: "00:00", d: "the day closes, snapshot taken" },
];

const BELIEF = [
  { k: "01", t: "SEES", d: "a resident notices something: a price, a crowd, a closed door" },
  { k: "02", t: "REMEMBERS", d: "it becomes an episodic memory, never edited afterwards" },
  { k: "03", t: "TALKS", d: "the kernel briefs the writer model; it drafts the lines; the kernel can refuse them" },
  { k: "04", t: "REACTS", d: "the listener accepts, doubts or refuses, drawn from trust and personality" },
  { k: "05", t: "BELIEVES", d: "accepted claims become beliefs, stamped with where they came from" },
];

const CHAIN = [
  "EVERY EVENT IS WRITTEN DOWN",
  "INTO A TAMPER-PROOF CHAIN",
  "THAT REPLAYS EXACTLY",
  "FORKS AT ANY DAY",
  "AND COMPARES FAIRLY",
];

const panel = "border-2 border-gb-line bg-gb-card p-6 sm:p-8";

export default function Systems() {
  return (
    <section id="systems" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="text-gb-neon text-[11px] tracking-[0.3em] mb-3">04 · UNDER THE HOOD</p>
          <h2 className="font-title text-6xl sm:text-7xl lg:text-8xl uppercase text-gb-pale leading-[0.85]">
            How the town runs
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-gb-light/90">
            No black box. The whole system in one diagram, then three more
            in plain words.
          </p>
        </motion.div>

        {/* the whole system — main diagram */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          transition={{ duration: 0.8 }}
          className="border-2 border-gb-line bg-gb-card p-4 sm:p-8 mb-4"
          data-testid="systems-main-diagram"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center justify-center w-9 h-9 border-2 border-gb-accent/50 text-gb-neon shrink-0">
              <Waypoints size={17} />
            </span>
            <h3 className="font-title text-2xl sm:text-3xl text-gb-pale">THE WHOLE SYSTEM</h3>
          </div>
          <div className="overflow-x-auto">
            <svg viewBox="0 0 940 1200" className="w-full h-auto min-w-[560px]" role="img" aria-label="Diagram of the whole system: scenario file, kernel, minds, choice points, models, validation, ledger, forks, viewer">
              <defs>
                <marker id="ahN" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                  <path d="M0,0 L8,4 L0,8 z" fill="#40F070" />
                </marker>
                <marker id="ahA" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                  <path d="M0,0 L8,4 L0,8 z" fill="#8BAC0F" />
                </marker>
              </defs>

              {/* boxes */}
              {[
                { id: "1", y: 16, h: 80, t: "SCENARIO FILE", lines: ["map, institutions, economy, health, groups,", "weather, interventions, seed"] },
                { id: "2", y: 136, h: 104, t: "KERNEL: DETERMINISTIC TRUTH", lines: ["event queue in seconds; movement on the tile grid,", "cars, buses, subway; accounts in cents; capacities;", "health; weather; interventions in force"] },
                { id: "3", y: 280, h: 80, t: "RESIDENTS' MINDS", lines: ["observations → memories → beliefs with", "provenance; plans; mind file"] },
                { id: "4", y: 400, h: 80, t: "CHOICE POINTS", lines: ["reflexes → obligations → commitments →", "utility policy → model deliberation"] },
                { id: "6", y: 660, h: 104, t: "VALIDATION AND AUDIT", lines: ["every model output checked against the legal", "options and the facts; accepted, refused,", "quarantined; the audit block on every decision"] },
                { id: "7", y: 804, h: 92, t: "HASH-CHAINED LEDGER", lines: ["events, observations, memories, belief versions,", "model calls, snapshots at midnight"] },
                { id: "8", y: 936, h: 104, t: "REPLAY, FORKS, NULL TWIN", lines: ["exact replay; fork at any day with a declared", "intervention; a fork with one extra random draw as", "the noise floor; comparison at 7, 14, 30 days"] },
                { id: "9", y: 1080, h: 92, t: "VIEWER AND PUBLIC CITY", lines: ["map, speech bubbles, lifelines, chronicle,", "newspaper, observer numbers, What-if page, Connect"] },
              ].map((b) => (
                <g key={b.id} data-testid={`diagram-box-${b.id}`}>
                  <rect x={240} y={b.y} width={460} height={b.h} fill="#0B160B" stroke="rgba(139,172,15,0.45)" strokeWidth="1.5" />
                  <text x={470} y={b.y + 30} textAnchor="middle" fontFamily="'VT323', monospace" fontSize="24" fill="#E0F8D8">
                    {b.t}
                  </text>
                  {b.lines.map((ln, k) => (
                    <text key={k} x={470} y={b.y + 52 + k * 16} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="11" fill="rgba(155,188,15,0.9)">
                      {ln}
                    </text>
                  ))}
                </g>
              ))}

              {/* box 5: Qwen writes, Jev decides */}
              <g data-testid="diagram-box-5">
                <rect x={240} y={520} width={270} height={100} fill="#0B160B" stroke="rgba(139,172,15,0.45)" strokeWidth="1.5" />
                <text x={375} y={550} textAnchor="middle" fontFamily="'VT323', monospace" fontSize="24" fill="#E0F8D8">
                  QWEN 2.5 7B: WORDS
                </text>
                <text x={375} y={572} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="11" fill="rgba(155,188,15,0.9)">
                  writes conversation lines, reflections,
                </text>
                <text x={375} y={588} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="11" fill="rgba(155,188,15,0.9)">
                  replies to visitors, the chronicle,
                </text>
                <text x={375} y={604} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="11" fill="rgba(155,188,15,0.9)">
                  the newspaper
                </text>
                <rect x={530} y={520} width={170} height={100} fill="#0B160B" stroke="rgba(139,172,15,0.45)" strokeWidth="1.5" />
                <text x={615} y={550} textAnchor="middle" fontFamily="'VT323', monospace" fontSize="24" fill="#E0F8D8">
                  JEV: DECISIONS
                </text>
                <text x={615} y={572} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="11" fill="rgba(155,188,15,0.9)">
                  TypeSafe Jev 1.13
                </text>
                <text x={615} y={594} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="11" fill="rgba(155,188,15,0.9)">
                  a probability per option
                </text>
              </g>

              {/* adjacent arrows */}
              <line x1={470} y1={96} x2={470} y2={132} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={478} y={120} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">declares</text>

              <line x1={470} y1={240} x2={470} y2={276} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={478} y={264} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">what each resident perceives</text>

              <line x1={470} y1={360} x2={470} y2={396} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={478} y={384} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">beliefs, memories, plans</text>

              <line x1={375} y1={480} x2={375} y2={514} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={383} y={502} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">a brief: who, what, stance, length</text>

              <line x1={615} y1={480} x2={615} y2={514} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={623} y={492} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">a typed question with the</text>
              <text x={623} y={506} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">resident's records</text>

              <line x1={375} y1={620} x2={462} y2={654} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={368} y={650} textAnchor="end" fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">lines</text>

              <line x1={615} y1={620} x2={478} y2={654} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={628} y={636} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">a distribution</text>
              <text x={628} y={650} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">over the options</text>

              <line x1={470} y1={764} x2={470} y2={800} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={478} y={788} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">accepted and refused, both recorded</text>

              <line x1={470} y1={896} x2={470} y2={932} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={478} y={920} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">replay from genesis or any snapshot</text>

              <line x1={470} y1={1040} x2={470} y2={1076} stroke="#40F070" strokeWidth="1.5" markerEnd="url(#ahN)" />
              <text x={478} y={1064} fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">the experiment of the period</text>

              {/* side rails */}
              <path d="M240,712 H170 V188 H236" fill="none" stroke="#8BAC0F" strokeOpacity="0.7" strokeWidth="1.2" markerEnd="url(#ahA)" />
              <text transform="rotate(-90 158 450)" x={158} y={450} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">
                the kernel samples, records, applies
              </text>

              <path d="M700,188 H790 V838 H704" fill="none" stroke="#8BAC0F" strokeOpacity="0.7" strokeWidth="1.2" markerEnd="url(#ahA)" />
              <text transform="rotate(-90 806 510)" x={806} y={510} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">
                every event with its draws
              </text>

              <path d="M700,320 H850 V858 H704" fill="none" stroke="#8BAC0F" strokeOpacity="0.7" strokeWidth="1.2" markerEnd="url(#ahA)" />
              <text transform="rotate(-90 866 590)" x={866} y={590} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">
                every observation, memory, belief version
              </text>

              <path d="M240,838 H120 V1090 H236" fill="none" stroke="#8BAC0F" strokeOpacity="0.7" strokeWidth="1.2" markerEnd="url(#ahA)" />
              <text transform="rotate(-90 108 965)" x={108} y={965} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">
                live state, feeds, lifelines
              </text>

              <path d="M240,1110 H70 V168 H236" fill="none" stroke="#8BAC0F" strokeOpacity="0.7" strokeWidth="1.2" markerEnd="url(#ahA)" />
              <text transform="rotate(-90 56 635)" x={56} y={635} textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="10" fill="#8BAC0F">
                the one write: a visitor's Connect message
              </text>
            </svg>
          </div>
          <p className="mt-6 text-center font-pixel text-xl text-gb-mint">
            "Deterministic truth, stochastic life: the kernel owns money,
            place, capacity and time; Qwen writes the words, Jev weighs the
            genuine choices, and everything is recorded and replays."
          </p>
        </motion.div>

        {/* diagram A — one day */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className={`${panel} mb-4`}
          data-testid="systems-day-panel"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center justify-center w-9 h-9 border-2 border-gb-accent/50 text-gb-neon">
              <CloudSun size={17} />
            </span>
            <h3 className="font-title text-2xl sm:text-3xl text-gb-pale">DIAGRAM A · ONE DAY IN TOWN</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {DAY.map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: (i % 4) * 0.05, duration: 0.4 }}
                className="border border-gb-line bg-gb-bg px-4 py-3"
              >
                <span className="font-pixel text-xl text-gb-neon leading-none">{d.t}</span>
                <p className="mt-1 text-[11px] leading-snug text-gb-light/85">{d.d}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-5 text-[11px] tracking-[0.15em] text-gb-accent">
            THE CLOCK IS REAL: THE TOWN LIVES AT 1× SPEED, AND THE SAME DAY CAN BE RE-RUN LATER, MINUTE FOR MINUTE
          </p>
        </motion.div>

        {/* diagram B — belief flow */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className={`${panel} mb-4`}
          data-testid="systems-belief-panel"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center justify-center w-9 h-9 border-2 border-gb-accent/50 text-gb-neon">
              <MessageSquare size={17} />
            </span>
            <h3 className="font-title text-2xl sm:text-3xl text-gb-pale">DIAGRAM B · FROM A GLANCE TO A BELIEF</h3>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-2">
            {BELIEF.map((s, i) => (
              <Fragment key={s.k}>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: i * 0.07, duration: 0.45 }}
                  className="flex-1 border-2 border-gb-line bg-gb-bg p-4 hover:border-gb-accent/50 transition-colors"
                >
                  <span className="font-pixel text-lg text-gb-neon">{s.k}</span>
                  <h4 className="font-pixel text-xl text-gb-pale tracking-wide">{s.t}</h4>
                  <p className="mt-1 text-[11px] leading-snug text-gb-light/85">{s.d}</p>
                </motion.div>
                {i < BELIEF.length - 1 && (
                  <ArrowRight size={16} className="hidden lg:block self-center text-gb-accent/70 shrink-0" />
                )}
              </Fragment>
            ))}
          </div>
          <p className="mt-5 text-[11px] tracking-[0.15em] text-gb-accent">
            EVERY BELIEF KEEPS ITS RECEIPT: SAW IT · WAS TOLD · HEARD FROM A FRIEND OF A FRIEND
          </p>
        </motion.div>

        {/* diagram C — the safety net */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className={panel}
          data-testid="systems-ledger-panel"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center justify-center w-9 h-9 border-2 border-gb-accent/50 text-gb-neon">
              <ShieldCheck size={17} />
            </span>
            <h3 className="font-title text-2xl sm:text-3xl text-gb-pale">DIAGRAM C · THE SAFETY NET</h3>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
            {CHAIN.map((c, i) => (
              <Fragment key={i}>
                <motion.span
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: i * 0.07, duration: 0.4 }}
                  className="border border-gb-accent/50 bg-gb-bg px-3.5 py-2 text-[10px] sm:text-[11px] tracking-[0.12em] text-gb-pale"
                >
                  {c}
                </motion.span>
                {i < CHAIN.length - 1 && <ArrowRight size={14} className="text-gb-accent/70" />}
              </Fragment>
            ))}
          </div>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <span className="font-arcade text-[7px] text-gb-bg bg-gb-neon px-3 py-2 w-max">
              FAILS CLOSED ON ANY DIVERGENCE
            </span>
            <p className="text-[12px] leading-relaxed text-gb-light/85 max-w-xl">
              The town can be re-run from any day and produce the exact same
              history, and two histories can be compared against a no-change
              control, so a "result" is never just noise.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}